import { keyBy, mapValues, uniqBy } from 'lodash-es';
import { getContent, IContentContext } from 'src/contexts/ContentContext';
import { VENUE_ZONE_SECTION_GROUP_AREA_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  Listing,
  ListingStatus,
  SectionSectionGroup,
} from 'src/WebApiController';

export const toSectionGroupKey = ({
  ticketClassId,
  sectionGroupAreaType,
  ticketClassName,
  sectionGroupAreaName,
}: SectionSectionGroup) =>
  `${ticketClassId ?? ''}:${sectionGroupAreaType ?? ''}:${ticketClassName ?? ''}:${sectionGroupAreaName ?? ''}`;

export const toDisplayName = (
  sectionGroup: SectionSectionGroup,
  contentContext: IContentContext
) => {
  if (sectionGroup.sectionGroupAreaName) {
    return sectionGroup.sectionGroupAreaName;
  }
  if (sectionGroup.sectionGroupAreaType == null) {
    if (!sectionGroup.ticketClassName) {
      return sectionGroup.sectionGroupAreaName;
    }
    return `${sectionGroup.ticketClassName}: ${sectionGroup.sectionGroupAreaName}`;
  }

  const sectionGroupAreaContentId =
    VENUE_ZONE_SECTION_GROUP_AREA_TYPE_TO_CID[
      sectionGroup.sectionGroupAreaType
    ];

  return `${sectionGroup.ticketClassName}: ${getContent(
    sectionGroupAreaContentId,
    contentContext
  )}`;
};

export const toListingStats = (listings: Listing[]) => {
  const listingCount = listings.length;
  const sectionIds = new Set<number>();
  let soldCount = 0;
  let unsoldCount = 0;
  let listedCount = 0;
  for (const listing of listings) {
    if (listing.seating.sectionId) {
      sectionIds.add(listing.seating.sectionId);
    }
    soldCount += listing.soldQty ?? 0;
    unsoldCount += listing.unsoldQty ?? 0;
    if (listing.status === ListingStatus.Listed) {
      listedCount++;
    }
  }
  return {
    sectionCount: sectionIds.size.toLocaleString(),
    listingCount: listingCount.toLocaleString(),
    soldUnsold: `${soldCount} / ${unsoldCount}`,
    listedCount: listedCount.toLocaleString(),
  };
};

export const getOrderedSectionGroups = (
  sectionToGroupLookup: Record<string, SectionSectionGroup>,
  customSectionGroups: SectionSectionGroup[]
) => {
  const sortedSectionGroups = Object.values(sectionToGroupLookup).map(
    (ssr) => ({
      ...ssr,
      id: toSectionGroupKey(ssr),
    })
  );

  // Map over customSectionGroups and add unique IDs
  const customGroupsWithId = customSectionGroups.map((csg) => ({
    ...csg,
    id: toSectionGroupKey(csg),
  }));

  // Combine the sorted section groups and custom section groups
  return uniqBy(sortedSectionGroups.concat(customGroupsWithId), 'id').toSorted(
    (a, b) => a.sectionGroupRank - b.sectionGroupRank || a.areaRank - b.areaRank
  );
};

export const reorderSectionGroups = (
  sectionToGroupLookup: Record<string, SectionSectionGroup>,
  customSectionGroups: SectionSectionGroup[],
  idToRankLookup?: Record<string, number>
): [Record<string, SectionSectionGroup>, SectionSectionGroup[]] => {
  const rankLookup =
    idToRankLookup ??
    mapValues(
      keyBy(
        getOrderedSectionGroups(sectionToGroupLookup, customSectionGroups),
        'id'
      ),
      'sectionGroupRank'
    );

  // Update the original sectionSectionGroupLookup with the new ranks
  const newSectionSectionGroupLookup = mapValues(
    sectionToGroupLookup,
    (ssg) => {
      const ssgId = toSectionGroupKey(ssg);
      const sgRank = rankLookup[ssgId] ?? 1;
      ssg.sectionGroupRank = sgRank;
      return ssg;
    }
  );

  const newCustomSectionGroups = customSectionGroups.map((ssg) => {
    const ssgId = toSectionGroupKey(ssg);
    const sgRank = rankLookup[ssgId] ?? 1;
    ssg.sectionGroupRank = sgRank;
    return ssg;
  });

  return [newSectionSectionGroupLookup, newCustomSectionGroups];
};
